import { useState } from "react";
import { useForm, useFormState } from "react-final-form";
import { useTranslation } from "react-i18next";
import { shallowEqual } from "react-redux";
import { useDispatch, useSelector } from "src/store";
import { makeStyles } from "tss-react/mui";

import { ConfirmDialog } from "@dashboard/devices/components/shared/index";
import { selectProvisionings } from "@dashboard/provisioning/store/selectors";
import { Row } from "@dashboard/shared/styles";
import { useCurrentSubscriptionExpired, useIsWorkspaceFeatureLimitReached } from "@dashboard/workspaces/api/helpers";
import { Box, DialogContentText, useTheme } from "@mui/material";
import { DangerOutlinedButton, PrimaryOutlinedButton } from "@shared/CustomButton";
import { ControlledSelectField } from "@shared/form/SelectField/SelectField";
import { ControlledSelectValue } from "@shared/form/SelectField/SelectField.types";
import { showErrorToast, showSuccessToast } from "@shared/toasts/Toasts";

import { deleteProvisioning } from "../store";
import { selectProvisioningListStatus } from "../store/selectors";
import { Provisioning } from "../types";
import { defaultProvisioning } from "../utils";

import CreateProvisioningDialog from "./CreateProvisioningDialog";
import EditProvisioningNameDialog from "./EditProvisioningNameDialog";

const useStyles = makeStyles()((theme) => ({
    buttonContainer: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginTop: theme.spacing(2),
    },
}));

export const ProvisioningManagement = ({ onSelect }: { onSelect: (provisioningId: number) => void }) => {
    const { classes } = useStyles();
    const dispatch = useDispatch();
    const theme = useTheme();
    const { t } = useTranslation(["pages", "common", "modals"]);

    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);

    const parentForm = useForm();
    const { values: parentFormValues } = useFormState<Partial<Provisioning>>();
    const status = useSelector(selectProvisioningListStatus);
    const provisionings = useSelector(selectProvisionings, shallowEqual);
    const isOverLimit = useIsWorkspaceFeatureLimitReached("ProvisioningLimit");
    const isExpired = useCurrentSubscriptionExpired();

    const selectedProvisioningId = parentForm.getState().values?.id;
    const selectedProvisioning = provisionings.find((p) => p.id === selectedProvisioningId) || null;

    const onChange = (value: ControlledSelectValue<false> | undefined) => {
        const provisioning = provisionings.filter((item) => item.id === value)[0];

        if (provisioning) {
            parentForm.reset(provisioning);
            onSelect(provisioning.id);
        }
    };

    const getCreateButtonTooltip = () => {
        if (isOverLimit) {
            return t("pages:provisioningPage.tooltip.provisioningLimitReached");
        }
        if (isExpired) {
            return t("pages:provisioningPage.tooltip.subscriptionExpired");
        }
        return "";
    };

    const onDelete = async (provisioningId: number | undefined) => {
        if (provisioningId) {
            await dispatch(deleteProvisioning(provisioningId))
                .unwrap()
                .then(() => {
                    showSuccessToast(t("pages:provisioningPage.toast.provisioningDeletedSuccessfully"));
                    const provisioning = provisionings.filter((item) => item.id !== provisioningId)[0];
                    parentForm.reset(provisioning || defaultProvisioning);
                    if (provisioning) {
                        onSelect(provisioning.id);
                    }
                })
                .catch(({ message = t("pages:provisioningPage.toast.provisioningDeletionFailed") }) =>
                    showErrorToast(message || t("pages:provisioningPage.toast.provisioningDeletionFailed")),
                )
                .finally(() => setDeleteDialogOpen(false));
        }
    };

    return (
        <>
            <Box style={{ paddingBottom: theme.spacing(2) }}>
                <ControlledSelectField
                    name="id"
                    label={
                        provisionings.length
                            ? t("pages:provisioningPage.input.id.caption1")
                            : t("pages:provisioningPage.input.id.caption2")
                    }
                    disabled={parentForm.getState().submitting || status === "pending" || !provisionings.length}
                    options={provisionings.map((item: Provisioning) => ({
                        label: item.name,
                        value: item.id,
                    }))}
                    fullWidth
                    onChange={onChange}
                />

                <Row className={classes.buttonContainer}>
                    <PrimaryOutlinedButton
                        name="rename"
                        disabled={parentForm.getState().submitting || isOverLimit || isExpired || !provisionings.length}
                        tooltipProps={{ title: t("common:tooltip.editName") }}
                        onClick={() => setEditDialogOpen(true)}
                    >
                        {t("common:button.rename")}
                    </PrimaryOutlinedButton>
                    <div>
                        <DangerOutlinedButton
                            name="delete"
                            style={{ marginRight: theme.spacing(1) }}
                            disabled={parentForm.getState().submitting || !provisionings.length}
                            onClick={() => setDeleteDialogOpen(true)}
                            data-testid="provisioningPage.btn.delete"
                        >
                            {t("common:button.delete")}
                        </DangerOutlinedButton>
                        <PrimaryOutlinedButton
                            name="create"
                            disabled={parentForm.getState().submitting || isOverLimit || isExpired}
                            onClick={() => setCreateDialogOpen(true)}
                            data-testid="provisioningPage.btn.create"
                            tooltipProps={{ title: getCreateButtonTooltip() }}
                        >
                            {t("common:button.create")}
                        </PrimaryOutlinedButton>
                    </div>
                </Row>
            </Box>
            <CreateProvisioningDialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} onCreated={onSelect} />
            {selectedProvisioning ? (
                <EditProvisioningNameDialog
                    open={editDialogOpen}
                    onClose={() => setEditDialogOpen(false)}
                    provisioning={selectedProvisioning}
                />
            ) : null}
            <ConfirmDialog
                title={t("modals:deleteProvisioning.title")}
                dangerButton
                primaryActionText={t("common:button.delete")}
                open={deleteDialogOpen}
                onConfirm={() => onDelete(parentFormValues?.id)}
                onClose={() => setDeleteDialogOpen(false)}
            >
                <DialogContentText>
                    {t("modals:deleteProvisioning.description", { name: parentForm.getState().values?.name })}
                </DialogContentText>
            </ConfirmDialog>
        </>
    );
};
